import { useCallback, useEffect, useState, type ReactNode, type FormEvent } from 'react';
import { apiRequest } from '../../api/client.js';
import { useAuth } from '../../auth/AuthProvider.js';
import { useTranslation } from '../../../../shared/i18n/index.js';
import { DataGrid } from '../../components/DataGrid.js';
import { MdmShell, Field, StatusBadge, ErrorAlert, s } from './_mdmKit.js';
import { Button } from '../../../../shared/components/index.js';

// M05 §7/§8 (T062) — Work Type Master. CRUD over the existing backend
// `@Controller('v1/master/work-types')`:
//   GET  /master/work-types?status → T062[]  (plain array)
//   POST /master/work-types (CreateWorkTypeDto) · PUT /master/work-types/:id
//   PATCH /master/work-types/:id/deactivate
// UI-Audit C6: surfaces the spec fields defaultTatCode / defaultWorkflowCode /
// defaultVendorCategory that already exist on the backend entity (no schema change).

interface WorkType {
  readonly workTypeId: string;
  readonly workTypeCode: string;
  readonly workTypeName: string;
  readonly category: string;
  readonly description: string | null;
  readonly defaultTatCode: string | null;
  readonly defaultWorkflowCode: string | null;
  readonly defaultVendorCategory: string | null;
  readonly recordStatusCode: string;
}

const STATUSES = ['ACTIVE', 'INACTIVE', 'ARCHIVED', 'PENDING_APPROVAL', 'REJECTED'] as const;

const EMPTY = {
  workTypeCode: '', workTypeName: '', category: '', description: '',
  defaultTatCode: '', defaultWorkflowCode: '', defaultVendorCategory: '',
};
type Form = typeof EMPTY;

export function WorkTypeMasterPage(): ReactNode {
  const { token } = useAuth();
  const { t } = useTranslation();
  const [rows, setRows] = useState<WorkType[] | null>(null);
  const [statusFilter, setStatusFilter] = useState('');
  const [error, setError] = useState<string | null>(null);

  const [form, setForm] = useState<Form>(EMPTY);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [showForm, setShowForm] = useState(false);
  const [formError, setFormError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    setRows(null);
    try {
      const params = new URLSearchParams();
      if (statusFilter) params.set('status', statusFilter);
      const qs = params.toString();
      const r = await apiRequest<WorkType[]>(`/master/work-types${qs ? `?${qs}` : ''}`, { token });
      setRows(Array.isArray(r) ? r : []);
      setError(null);
    } catch (e) { setError((e as Error).message); setRows([]); }
  }, [token, statusFilter]);

  useEffect(() => { void load(); }, [load]);

  const set = (k: keyof Form) => (e: { target: { value: string } }) =>
    setForm((f) => ({ ...f, [k]: e.target.value }));

  const beginCreate = () => { setForm(EMPTY); setEditingId(null); setFormError(null); setShowForm(true); };
  const beginEdit = (w: WorkType) => {
    setForm({
      workTypeCode: w.workTypeCode, workTypeName: w.workTypeName, category: w.category,
      description: w.description ?? '', defaultTatCode: w.defaultTatCode ?? '',
      defaultWorkflowCode: w.defaultWorkflowCode ?? '', defaultVendorCategory: w.defaultVendorCategory ?? '',
    });
    setEditingId(w.workTypeId); setFormError(null); setShowForm(true);
  };

  const onSubmit = useCallback(async (e: FormEvent) => {
    e.preventDefault();
    if (!form.workTypeName.trim() || !form.category.trim()) { setFormError(t('mdm.workType.nameAndCategoryReq')); return; }
    setSaving(true); setFormError(null);
    const trimmed = (x: string) => (x.trim() ? x.trim() : undefined);
    try {
      if (editingId) {
        await apiRequest(`/master/work-types/${encodeURIComponent(editingId)}`, {
          method: 'PUT', token,
          body: {
            workTypeName: form.workTypeName.trim(), category: form.category.trim(),
            description: trimmed(form.description), defaultTatCode: trimmed(form.defaultTatCode),
            defaultWorkflowCode: trimmed(form.defaultWorkflowCode), defaultVendorCategory: trimmed(form.defaultVendorCategory),
          },
        });
      } else {
        await apiRequest('/master/work-types', {
          method: 'POST', token,
          body: {
            workTypeCode: form.workTypeCode.trim(), workTypeName: form.workTypeName.trim(),
            category: form.category.trim(), description: trimmed(form.description),
            defaultTatCode: trimmed(form.defaultTatCode), defaultWorkflowCode: trimmed(form.defaultWorkflowCode),
            defaultVendorCategory: trimmed(form.defaultVendorCategory),
          },
        });
      }
      setShowForm(false); setForm(EMPTY); setEditingId(null);
      await load();
    } catch (err) { setFormError((err as Error).message); }
    finally { setSaving(false); }
  }, [editingId, form, token, load]);

  const onDeactivate = useCallback(async (w: WorkType) => {
    setError(null);
    try {
      await apiRequest(`/master/work-types/${encodeURIComponent(w.workTypeId)}/deactivate`, { method: 'PATCH', token });
      await load();
    } catch (e) { setError((e as Error).message); }
  }, [token, load]);

  return (
    <MdmShell
      label="work-type-master"
      title={t('mdm.workType.title')}
      description={t('mdm.workType.description')}
      actions={<Button type="button" variant="primary" data-testid="new-work-type" onClick={beginCreate}>{t('mdm.workType.newWorkType')}</Button>}
    >
      <form aria-label="work-type-filters" onSubmit={(e) => { e.preventDefault(); void load(); }}
            style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'flex-end', marginBottom: 16 }}>
        <Field id="wt-status" label={t('mdm.col.status')}>
          <select id="wt-status" value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)} style={s.input}>
            <option value="">{t('mdm.common.all')}</option>
            {STATUSES.map((x) => <option key={x} value={x}>{x}</option>)}
          </select>
        </Field>
        <Button type="submit" variant="secondary" data-testid="apply-filters">{t('mdm.common.search')}</Button>
      </form>

      {showForm && (
        <form onSubmit={onSubmit} aria-label={editingId ? 'edit-work-type-form' : 'create-work-type-form'} style={s.card}>
          <h2 style={{ fontSize: 15, marginTop: 0 }}>{editingId ? t('mdm.workType.editTitle') : t('mdm.workType.newTitle')}</h2>
          <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap' }}>
            <Field id="w-workTypeCode" label={t('mdm.workType.codeReq')}>
              <input id="w-workTypeCode" value={form.workTypeCode} onChange={set('workTypeCode')} required disabled={!!editingId} style={s.input} />
            </Field>
            <Field id="w-workTypeName" label={t('mdm.workType.nameReq')}>
              <input id="w-workTypeName" value={form.workTypeName} onChange={set('workTypeName')} required style={s.input} />
            </Field>
            <Field id="w-category" label={t('mdm.workType.categoryReq')}>
              <input id="w-category" value={form.category} onChange={set('category')} required style={s.input} />
            </Field>
            <Field id="w-description" label={t('mdm.col.description')}>
              <input id="w-description" value={form.description} onChange={set('description')} style={s.input} />
            </Field>
            <Field id="w-defaultTatCode" label={t('mdm.workType.defaultTat')} hint={t('mdm.workType.defaultTatHint')}>
              <input id="w-defaultTatCode" value={form.defaultTatCode} onChange={set('defaultTatCode')} style={s.input} />
            </Field>
            <Field id="w-defaultWorkflowCode" label={t('mdm.workType.defaultWorkflow')}>
              <input id="w-defaultWorkflowCode" value={form.defaultWorkflowCode} onChange={set('defaultWorkflowCode')} style={s.input} />
            </Field>
            <Field id="w-defaultVendorCategory" label={t('mdm.workType.defaultVendor')}>
              <input id="w-defaultVendorCategory" value={form.defaultVendorCategory} onChange={set('defaultVendorCategory')} style={s.input} />
            </Field>
          </div>
          <ErrorAlert message={formError} />
          <div style={{ display: 'flex', gap: 8, marginTop: 12 }}>
            <Button type="submit" variant="primary" data-testid="save-work-type" disabled={saving}>
              {saving ? t('mdm.common.saving') : editingId ? t('mdm.common.saveChanges') : t('mdm.workType.createWorkType')}
            </Button>
            <Button type="button" variant="secondary" onClick={() => setShowForm(false)}>{t('common.cancel')}</Button>
          </div>
        </form>
      )}

      <ErrorAlert message={error} />
      {!rows ? (
        <p>{t('common.loading')}</p>
      ) : rows.length === 0 ? (
        <p data-testid="work-types-empty" style={{ color: s.muted }}>{t('mdm.workType.empty')}</p>
      ) : (
        <DataGrid<WorkType>
          caption="work-types-table"
          rows={rows}
          getRowId={(w) => w.workTypeId}
          rowTestId={(w) => `work-type-row-${w.workTypeId}`}
          columns={[
            { key: 'code', header: t('mdm.col.code'), value: (w) => w.workTypeCode },
            { key: 'name', header: t('mdm.col.name'), value: (w) => w.workTypeName },
            { key: 'category', header: t('mdm.workType.colCategory'), value: (w) => w.category },
            { key: 'defaultTat', header: t('mdm.workType.colDefaultTat'), value: (w) => w.defaultTatCode ?? '—' },
            { key: 'defaultWorkflow', header: t('mdm.workType.colDefaultWorkflow'), value: (w) => w.defaultWorkflowCode ?? '—' },
            { key: 'vendorCategory', header: t('mdm.workType.colVendorCategory'), value: (w) => w.defaultVendorCategory ?? '—' },
            { key: 'status', header: t('mdm.col.status'), value: (w) => w.recordStatusCode, render: (w) => <StatusBadge status={w.recordStatusCode} /> },
          ]}
          actionsHeader={t('mdm.col.actions')}
          actions={(w) => (
            <div style={{ display: 'flex', gap: 6 }}>
              <Button type="button" variant="secondary" data-testid={`edit-${w.workTypeId}`} onClick={() => beginEdit(w)}>{t('mdm.common.edit')}</Button>
              {w.recordStatusCode === 'ACTIVE' && (
                <Button type="button" variant="danger" data-testid={`deactivate-${w.workTypeId}`} onClick={() => onDeactivate(w)}>{t('mdm.common.deactivate')}</Button>
              )}
            </div>
          )}
        />
      )}
    </MdmShell>
  );
}
